import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth, useUser } from '@clerk/clerk-react';
import { useParams, Link } from 'react-router-dom';

import { createApiClient } from '../api.js';
import { UserSearchInput } from '../components/UserSearchInput.jsx';

/**
 * Per-user progress drill-down — what tours / content / checklists
 * has this user actually interacted with on this tenant.  Pick a
 * user from the search widget below (name, email, or id) or follow
 * a "View user" link from the audit log or a Pinpoint thread.
 */
export function UserProgress() {
  const { getToken } = useAuth();
  const { user: clerkUser } = useUser();
  const { user_id: routeUserId } = useParams();
  const api = useMemo(() => createApiClient(getToken), [getToken]);

  const me = useMemo(() => {
    if (!clerkUser) return null;
    return {
      user_id: clerkUser.id,
      display_name: clerkUser.fullName || clerkUser.firstName || '',
      email: clerkUser.primaryEmailAddress?.emailAddress || '',
      avatar_url: clerkUser.imageUrl || '',
    };
  }, [clerkUser]);

  // Currently-resolved user_id — drives the lookup.  The search
  // widget hands us the bare id on pick; the rest of the page reads
  // from `selectedUserId` so a programmatic clear (e.g. route nav)
  // resets cleanly.
  const [selectedUserId, setSelectedUserId] = useState(routeUserId || '');
  const [progress, setProgress] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const lookup = useCallback(async (uid) => {
    if (!uid) {
      setProgress(null);
      setEvents([]);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [p, e] = await Promise.all([
        api(`/v1/admin/users/${encodeURIComponent(uid)}/progress`),
        api(`/v1/admin/users/${encodeURIComponent(uid)}/events?limit=50`).catch(() => null),
      ]);
      setProgress(p);
      setEvents(e?.items || []);
    } catch (ex) {
      setError(ex.message);
    } finally {
      setLoading(false);
    }
  }, [api]);

  // Trigger a lookup whenever the picked id changes — either from
  // the search widget or from the /users/:user_id route param.
  useEffect(() => { lookup(selectedUserId); }, [lookup, selectedUserId]);

  return (
    <div className="page">
      <div className="page__title">
        <div>
          <h1>User progress</h1>
          <p className="page__subtitle">
            What tours, content, and checklists has a specific user
            touched? Search by name, email, or id — or follow a
            "View user" link from the
            {' '}<Link to="/audit">audit log</Link> or a Pinpoint
            inbox thread.
          </p>
        </div>
      </div>

      <div style={{ marginBottom: 12, maxWidth: 520 }}>
        <UserSearchInput
          value={selectedUserId}
          onChange={setSelectedUserId}
          api={api}
          placeholder="Search by name, email, or user id…"
          autoFocus
          currentUser={me}
        />
      </div>

      {loading && <div className="state"><div className="state__title">Looking up user…</div></div>}
      {error && <div className="state state--error">{error}</div>}

      {progress && (
        <>
          <section className="card" style={{ marginBottom: 14 }}>
            <h3 className="card__title">Summary</h3>
            <div className="user-progress__counters">
              <Counter label="Tours completed" value={progress.summary?.tours_completed ?? 0} tone="ok" />
              <Counter label="Tours in progress" value={progress.summary?.tours_in_progress ?? 0} />
              <Counter label="Content dismissed" value={progress.summary?.content_dismissed ?? 0} tone="muted" />
              <Counter label="Checklists completed" value={progress.summary?.checklists_completed ?? 0} tone="ok" />
            </div>
          </section>

          <ProgressTable title="Tours" rows={progress.tours} />
          <ProgressTable title="Content" rows={progress.content} />
          <ProgressTable title="Checklists" rows={progress.checklists} />

          {events.length > 0 && (
            <section className="card" style={{ marginTop: 14 }}>
              <h3 className="card__title">Recent events ({events.length})</h3>
              <table className="resource-table" style={{ width: '100%' }}>
                <thead>
                  <tr><th>When</th><th>Event</th><th>Content</th><th>Session</th></tr>
                </thead>
                <tbody>
                  {events.slice(0, 50).map((ev, i) => (
                    <tr key={i}>
                      <td style={{ fontVariantNumeric: 'tabular-nums' }}>{formatWhen(ev.timestamp)}</td>
                      <td><code>{ev.event_type}</code></td>
                      <td><code>{ev.content_id || '—'}</code></td>
                      <td style={{ color: 'var(--text-3)' }}>{(ev.session_id || '').slice(0, 12)}…</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}
        </>
      )}

      <style>{UP_CSS}</style>
    </div>
  );
}

function Counter({ label, value, tone }) {
  return (
    <div className={`up-counter up-counter--${tone || 'plain'}`}>
      <div className="up-counter__value">{value}</div>
      <div className="up-counter__label">{label}</div>
    </div>
  );
}

function ProgressTable({ title, rows }) {
  if (!rows || rows.length === 0) return null;
  return (
    <section className="card" style={{ marginTop: 14 }}>
      <h3 className="card__title">{title} ({rows.length})</h3>
      <table className="resource-table" style={{ width: '100%' }}>
        <thead>
          <tr><th>Title</th><th>Status</th><th>Progress</th><th>Started</th><th>Last</th></tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.content_id}>
              <td>
                <strong>{r.title || <em style={{ color: 'var(--text-3)' }}>(removed)</em>}</strong>
                <div style={{ color: 'var(--text-3)', fontSize: 11.5 }}><code>{r.content_id}</code></div>
              </td>
              <td>
                <span className={`badge ${statusCls(r.status)}`}>{r.status}</span>
              </td>
              <td style={{ fontVariantNumeric: 'tabular-nums' }}>
                {r.current_step}/{r.total_steps || '?'}
              </td>
              <td>{formatWhen(r.started_at)}</td>
              <td>{formatWhen(r.updated_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function statusCls(status) {
  const s = (status || '').toLowerCase();
  if (s === 'completed') return 'badge--editor';
  if (s === 'in_progress' || s === 'started') return 'badge--admin';
  if (s === 'dismissed') return 'badge--default';
  return 'badge--viewer';
}

function formatWhen(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    if (diff < 86_400_000) return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return iso; }
}

const UP_CSS = `
  .user-progress__counters { display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 10px; margin-top: 8px; }
  .up-counter { padding: 12px 14px; border: 1px solid var(--border-1); border-radius: 10px; background: var(--surface-0); }
  .up-counter--ok { border-color: #86efac; background: #f0fdf4; }
  .up-counter--muted { background: var(--surface-1); }
  .up-counter__value { font-size: 22px; font-weight: 700; color: var(--text-1); }
  .up-counter__label { font-size: 12px; color: var(--text-3); margin-top: 2px; }
`;
